import * as random from "../../util/random.js";
import * as gsf from "../../game-state-functions.js";
import * as trajectoryUtil from "../../geometry/trajectory/trajectory-util.js";
import * as shapeSpatialRelations from "../../geometry/shape-spatial-relations.js";
import * as cc from "../../core-functions.js";
import { setCellStamp } from "../../util/cell-stamps.js";

var type = "wallHack";

// Where a worm at (x, y) heading `direction`, out through a stadium's wall,
// comes back in: through the middle. Its point on the spine (see
// shape-factory createStadium) is mirrored through the stadium's centre, and it
// re-enters on the far side as far inside as it had gone out, heading mirrored
// in the wall (the direction's part along the wall flips, the part across it
// stays, so it points back in). A stadium with no straight part is a circle,
// and this is then exactly the circle's rule. One rule for the whole wall, so
// sliding along the wall past a joint never jumps; any rule that went straight
// across on the flat sides and never jumped would send a worm leaving an end's
// tip back to that tip, onto its own trail.
function stadiumMirror(shape, x, y, direction) {
    var sx = shape.centerX + Math.min(Math.max(x - shape.centerX, -shape.halfSpine), shape.halfSpine);
    var sy = shape.centerY;
    var vx = x - sx, vy = y - sy;
    var r1 = Math.sqrt(vx * vx + vy * vy) || 1e-9;
    var nx = vx / r1, ny = vy / r1;
    // the mirrored spine point, and how far in from it (never past the spine)
    var mx = 2 * shape.centerX - sx, my = 2 * shape.centerY - sy;
    var k = Math.max(0, 2 * shape.radius - r1);
    var normalAngle = Math.atan2(ny, nx);
    return {
        x: mx - k * nx,
        y: my - k * ny,
        direction: 2 * normalAngle - direction
    };
}

function activate({ strength, duration, wormId }) {
    return {
        timeLeft: duration,
        strength: strength,
        wormId: wormId,
        type: type
    };
}

function updateWorm(gameState, deltaTime, wormId, pathSegment) {
    var worm = gsf.getWorm(gameState, wormId);
    if (!shapeSpatialRelations.contains(gameState.map.shape, worm)) {
        function getModifiedWallHackSegment({xDiff = 0, yDiff = 0, startDirectionDiff = 0}) {
            var modifiedSegment = gsf.createWormPathSegment(gameState, wormId,
                {
                    duration: pathSegment.duration,
                    centerX: pathSegment.startX + xDiff,
                    centerY: pathSegment.startY + yDiff,
                    direction: pathSegment.startDirection + startDirectionDiff,
                    speed: pathSegment.speed,
                    turningVelocity: pathSegment.turningVelocity,
                    jump: pathSegment.jump,
                    size: pathSegment.size
                });
            modifiedSegment.type = pathSegment.type;
            return modifiedSegment
        }

        var wallHackSegments;
        if (gameState.map.shape.type === "rectangle") {
            var width = gameState.map.shape.width;
            var height = gameState.map.shape.height;
            wallHackSegments = [
                getModifiedWallHackSegment({xDiff: -width}),
                getModifiedWallHackSegment({xDiff: width}),
                getModifiedWallHackSegment({yDiff: -height}),
                getModifiedWallHackSegment({yDiff: height}),
                getModifiedWallHackSegment({xDiff: -width, yDiff: -height}),
                getModifiedWallHackSegment({xDiff: width, yDiff: -height}),
                getModifiedWallHackSegment({xDiff: -width, yDiff: height}),
                getModifiedWallHackSegment({xDiff: width, yDiff: height})
            ];
        } else if (gameState.map.shape.type === "circle") {

            var x1 = gameState.map.shape.centerX - pathSegment.startX;
            var y1 = gameState.map.shape.centerY - pathSegment.startY;
            var r1 = Math.sqrt(Math.pow(x1, 2) + Math.pow(y1, 2));
            var d = gameState.map.shape.radius * 2;
            var centerX = d * x1 / r1 - x1 + gameState.map.shape.centerX;
            var centerY = d * y1 / r1 - y1 + gameState.map.shape.centerY;


            var oldDir = pathSegment.startDirection;
            x1 = pathSegment.startX - gameState.map.shape.centerX;
            y1 = pathSegment.startY - gameState.map.shape.centerY;
            r1 = Math.sqrt(Math.pow(x1, 2) + Math.pow(y1, 2));
            var dir1 = Math.acos(x1 / r1);
            if (y1 < 0) {
                dir1 += (Math.PI - dir1) * 2;
            }
            while (oldDir < 0) {
                oldDir += (2 * Math.PI);
            }
            while (oldDir > 2 * Math.PI) {
                oldDir -= (2 * Math.PI);
            }
            var newDir = oldDir + (dir1 - oldDir) * 2;

            wallHackSegments = [
                getModifiedWallHackSegment({
                    xDiff: centerX - pathSegment.startX,
                    yDiff: centerY - pathSegment.startY,
                    startDirectionDiff: newDir - pathSegment.startDirection
                })
            ];
        } else if (gameState.map.shape.type === "stadium") {
            var mirror = stadiumMirror(gameState.map.shape, pathSegment.startX, pathSegment.startY, pathSegment.startDirection);
            wallHackSegments = [
                getModifiedWallHackSegment({
                    xDiff: mirror.x - pathSegment.startX,
                    yDiff: mirror.y - pathSegment.startY,
                    startDirectionDiff: mirror.direction - pathSegment.startDirection
                })
            ];
        } else {
            throw "Unhandled map type for wall hack: " + gameState.map.shape.type;
        }

        var secondaryId = 1;
        wallHackSegments.forEach(function (segment) {
            if (shapeSpatialRelations.intersects(gameState.map.shape, {centerX: segment.endX, centerY: segment.endY, radius: segment.size})) {
                var segmentId = worm.trackId + "#" + secondaryId;
                segment.id = segmentId;
                secondaryId++;

                //  TODO: Will get removed when we no longer have collision detection based on playArea
                if (pathSegment.speed > 0 && !pathSegment.jump) {
                    //TODO Rename addPlayAreaWormHead
                    gsf.addPlayAreaShape(gameState, {centerX: segment.startX, centerY: segment.startY, radius: segment.size}, segmentId).forEach(function (cell) {
                        setCellStamp(worm, cell.index, worm.distanceTravelled);
                    });
                }
                gsf.addWormPathSegment(gameState, segment);
            }
        });

        if (!shapeSpatialRelations.intersects(gameState.map.shape, worm)) {
            // Main worm segment is outside of map, switch with one of the wallHackSegments, that should be fully inside the map
            var wallHackSegmentInsideMap = wallHackSegments.find(segment => shapeSpatialRelations.contains(gameState.map.shape, {centerX: segment.endX, centerY: segment.endY, radius: segment.size}));
            if (!wallHackSegmentInsideMap) {
                wallHackSegmentInsideMap = wallHackSegments.find(segment => shapeSpatialRelations.intersects(gameState.map.shape, {centerX: segment.endX, centerY: segment.endY, radius: segment.size}));
            }
            if (wallHackSegmentInsideMap) {
                worm.direction = wallHackSegmentInsideMap.startDirection;
                worm.centerX = wallHackSegmentInsideMap.endX;
                worm.centerY = wallHackSegmentInsideMap.endY;
            } else if (gameState.map.shape.type === "rectangle") {
                // No single-width shift lands on the map: the worm got further out than one
                // map width in one step (a long step after a stall, or a huge size). Wrap its
                // position around the map instead of throwing, which would stop the match loop.
                var shape = gameState.map.shape;
                worm.centerX = shape.x + ((((worm.centerX - shape.x) % shape.width) + shape.width) % shape.width);
                worm.centerY = shape.y + ((((worm.centerY - shape.y) % shape.height) + shape.height) % shape.height);
            } else if (gameState.map.shape.type === "stadium") {
                // Same safety net: mirror the head itself (never lands past the spine).
                var m = stadiumMirror(gameState.map.shape, worm.centerX, worm.centerY, worm.direction);
                worm.centerX = m.x;
                worm.centerY = m.y;
                worm.direction = m.direction;
            }
        }
    }
}

export {
    stadiumMirror,
    type,
    updateWorm,
    activate
};
